// src/components/FriendsList.jsx
import React, { useEffect, useState } from 'react';
import { getFriends, getFriendCount, removeFriend } from '../services/connectionService';
import '../styles/FriendsList.css';
import ErrorNotification from './ErrorNotification';

const FriendsList = ({ walletAddress, onClose, onChatOpen, unreadMessages, onFriendUpdate }) => {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [friendCount, setFriendCount] = useState({ count: 0, max: 3 });
  const [removingFriend, setRemovingFriend] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchFriends = async () => {
      try {
        setLoading(true);
        const friendsList = await getFriends(walletAddress);
        setFriends(friendsList);
        
        // Get friend count
        const countData = await getFriendCount(walletAddress);
        setFriendCount(countData);
        
        setLoading(false);
      } catch (error) {
        setError('Failed to load friends');
        setLoading(false);
      }
    };

    fetchFriends();
    const interval = setInterval(fetchFriends, 30000);
    return () => clearInterval(interval);
  }, [walletAddress]);

  // Format wallet address for display
  const formatWallet = (wallet) => {
    if (!wallet) return '';
    return `${wallet.substring(0, 6)}...${wallet.substring(wallet.length - 4)}`;
  };
  
  // Handle removing a friend
  const handleRemoveFriend = async (friendWalletAddress) => {
    // Show confirmation modal
    setConfirmationModal(friendWalletAddress);
  };
  
  // Confirm friend removal
  // Update error handling in the removeFriend function:
const confirmRemoveFriend = async () => {
  const friendWalletAddress = confirmationModal;
  if (!friendWalletAddress) return;
  
  try {
    setRemovingFriend(friendWalletAddress);
    await removeFriend(walletAddress, friendWalletAddress);
    
    // Update local state
    setFriends(friends.filter(friend => friend.walletAddress !== friendWalletAddress));
    setFriendCount(prev => ({ ...prev, count: prev.count - 1 }));
    
    // Notify parent component about the change
    if (onFriendUpdate) {
      onFriendUpdate();
    }
    
    setRemovingFriend(null);
    setConfirmationModal(null);
  } catch (error) {
    // Update both local error state and UI notification
    setError('Failed to remove friend: ' + (error.message || 'Unknown error'));
    setErrorMessage('Failed to remove friend: ' + (error.message || 'Unknown error'));
    setRemovingFriend(null);
  }
};
  
  // Cancel friend removal
  const cancelRemoveFriend = () => {
    setConfirmationModal(null);
  };

  return (
    <div className="friends-list-container">
      <div className="friends-list-header">
        <h2>Friends</h2>
        <div className="friend-count">
          {friendCount.count} / {friendCount.max}
        </div>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-indicator"></div>
          <p>Loading friends...</p>
        </div>
      ) : error ? (
        <p className="error-message">{error}</p>
      ) : friends.length === 0 ? (
        <div className="empty-state">
          <p>You don't have any friends yet</p>
          <p className="empty-state-hint">Use the "Add Friend" button to connect with others</p>
        </div>
      ) : (
        <>
          <ul className="friends-list">
            {friends.map((friend) => (
              <li key={friend.walletAddress} className="friend-item">
                <div className="friend-info">
                  <span className="username">{friend.username}</span>
                  <span className="wallet">{formatWallet(friend.walletAddress)}</span>
                </div>
                <div className="friend-actions">
                  <button 
                    className="chat-btn"
                    onClick={() => onChatOpen(friend)}
                  >
                    Chat
                    {unreadMessages[friend.walletAddress] > 0 && (
                      <span className="unread-badge">{unreadMessages[friend.walletAddress]}</span>
                    )}
                  </button>
                  <button 
                    className="remove-btn"
                    disabled={removingFriend === friend.walletAddress}
                    onClick={() => handleRemoveFriend(friend.walletAddress)}
                  >
                    {removingFriend === friend.walletAddress ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
          
          {/* Confirmation Modal */}
          {confirmationModal && (
            <div className="confirmation-modal">
              <div className="confirmation-content">
                <h3>Remove Friend</h3>
                <p>Are you sure you want to remove this friend? You will no longer be able to chat with them.</p>
                <div className="confirmation-actions">
                  <button className="cancel-btn" onClick={cancelRemoveFriend}>Cancel</button>
                  <button className="confirm-btn" onClick={confirmRemoveFriend}>Remove</button>
                </div>
              </div>
              <ErrorNotification 
  message={errorMessage}
  onClose={() => setErrorMessage('')}
/>
            </div>
            
          )}
        </>
      )}
    </div>
  );
};

export default FriendsList;